// Copyright (c) 2020 WiseTime. All rights reserved.

import { flatten, uniq } from "ramda"
import { i18n } from "@lingui/core"
import { t } from "@lingui/macro"
import { SiteId } from "../user-context/slice"
import { TimeGroup, TimeRow, TrashStatus } from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { sortTagsListAlphabetically } from "./sorting"

export const getMatchedTimeRowsForGroup = (
  timeRows: TimeRow.AsObject[],
  timeGroup: TimeGroup.AsObject,
  currentSiteId: SiteId | undefined,
): TimeRow.AsObject[] => {
  const groupTagNames = timeGroup.tagsList
    .filter(t => t.siteId === currentSiteId)
    .map(groupTag => groupTag.name)

  if (groupTagNames.length === 0) {
    return []
  }

  return timeRows
    .filter(row => row.trashStatus !== TrashStatus.TRASHED
      && (row.timeGroupId === undefined || row.timeGroupId === "")
      && row.tagsList.filter(t => t.siteId === currentSiteId).some(rowTag => groupTagNames.includes(rowTag.name))
    )
}

export const extractUniqueTagIdsFromTimeRows = (
  timeRows: TimeRow.AsObject[],
  currentSiteId: SiteId | undefined,
): string[] => {
  return uniq(flatten(
    timeRows.map(timeRow => timeRow.tagsList
      .filter(t => t.siteId === currentSiteId)
      .map(t => t.id))
  ))
}

export const extractUniqueTagCategoryIdsFromTimeRows = (
  timeRows: TimeRow.AsObject[],
  currentSiteId: SiteId | undefined,
): string[] => {
  return uniq(flatten(
    timeRows.map(timeRow => timeRow.tagCategoriesList
      .filter(t => t.siteId === currentSiteId)
      .map(t => t.id))
  ))
}

export const getMatchedTagTimeRows = (
  selectedTimeRows: TimeRow.AsObject[],
  timeRowsForDate: TimeRow.AsObject[],
  currentSiteId: SiteId | undefined,
): TimeRow.AsObject[] => {
  const tagIdsFromSelectedRows = extractUniqueTagIdsFromTimeRows(selectedTimeRows, currentSiteId)
  const tagCategoryIdsFromSelectedRows = extractUniqueTagCategoryIdsFromTimeRows(selectedTimeRows, currentSiteId)
  if (tagIdsFromSelectedRows.length === 0 && tagCategoryIdsFromSelectedRows.length === 0) {
    return []
  }

  const hasSameTagOrTagCategory = (timeRow: TimeRow.AsObject) =>
    timeRow.tagsList.filter(t => t.siteId === currentSiteId)
      .some(({ id }) => tagIdsFromSelectedRows.includes(id)) ||
    timeRow.tagCategoriesList.filter(t => t.siteId === currentSiteId)
      .some(({ id }) => tagCategoryIdsFromSelectedRows.includes(id))

  const selectedTimeRowIds = selectedTimeRows.map(r => r.id)
  return timeRowsForDate.filter(row => !selectedTimeRowIds.includes(row.id)
        && row.trashStatus !== TrashStatus.TRASHED
        && row.timeGroupId === ""
        && hasSameTagOrTagCategory(row))
}

export const hasTimeGroup = (row: TimeRow.AsObject): boolean => Boolean(row.timeGroupId)

export const hasTrashedItems = (timeRows: TimeRow.AsObject[]): boolean =>
  timeRows.some(timeRow => timeRow.trashStatus === TrashStatus.TRASHED)

export const countUnpostedUntrashedUngroupedRows = (timeRows: TimeRow.AsObject[]): number =>
  timeRows.filter(timeRow => timeRow.trashStatus !== TrashStatus.TRASHED && timeRow.timeGroupId === "").length

export const getDescription = (description: string): string => {
  if (description === "@_empty_@") {
    return "-- " + i18n._(t({ id: "msg_no_window_title_available", message: "No window title available" })) + " --"
  }
  if (description === "@_blank_@") {
    return "-- " + i18n._(t({ id: "msg_no_window_title_found", message: "No window title found" })) + " --"
  }
  return description
}

type TimeRowsByTags = Record<string, TimeRow.AsObject[]>
export const groupTimeRowsByTags = (timeRows: TimeRow.AsObject[], currentSiteId?: SiteId) =>
  timeRows.reduce((result: TimeRowsByTags, timeRow: TimeRow.AsObject) => {
    const key = timeRow.tagsList
      .filter(t => t.siteId === currentSiteId)
      .sort(sortTagsListAlphabetically)
      .map(t => t.name)
      .join("")
    if (result[key] === undefined) {
      result[key] = [timeRow]
    } else {
      result[key]?.push(timeRow)
    }
    return result
  }, {})
